import { LOCALES, localeByCode, localisePath } from '@/i18n/locales.js'

/**
 * What a search engine needs to index a translated page correctly.
 *
 * A single-page app serves one HTML file for every URL, so the head has to
 * be rewritten on each navigation: a canonical link for the page in its own
 * language, one `hreflang` alternate per language the page exists in, an
 * `x-default` pointing at English, and a title and description in the
 * reader's language. Google follows the alternates to index `/tl/terms` as
 * the Tagalog page rather than a duplicate of `/terms`.
 *
 * Only public pages get this. The panel and admin are signed-in and carry
 * `noindex` instead.
 */

const SITE = (import.meta.env.VITE_SITE_URL || window.location.origin).replace(/\/$/, '')

/** The public routes, by name, with the languages each exists in. */
const PUBLIC_PAGES = {
  home: { path: '/', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  'public-services': { path: '/services', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  faq: { path: '/faq', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  help: { path: '/help', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  terms: { path: '/terms', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  privacy: { path: '/privacy', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  login: { path: '/login', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
  register: { path: '/register', translated: ['en', 'tl', 'ceb', 'th', 'vi'] },
}

/** Titles and descriptions per page per language. English is the fallback. */
const COPY = {
  terms: {
    en: [
      'Terms of Service',
      'What you agree to when you use PresenceRise: orders, the prepaid wallet, refunds, refills, and your account.',
    ],
    tl: [
      'Mga Tuntunin ng Serbisyo',
      'Ang sinasang-ayunan mo kapag ginamit mo ang PresenceRise: mga order, prepaid wallet, refund, refill, at ang account mo.',
    ],
    ceb: [
      'Mga Termino sa Serbisyo',
      'Ang imong giuyonan kung gamiton nimo ang PresenceRise: mga order, prepaid wallet, refund, refill, ug ang imong account.',
    ],
    th: [
      'ข้อกำหนดการให้บริการ',
      'สิ่งที่คุณตกลงเมื่อใช้ PresenceRise: ออร์เดอร์ wallet แบบเติมเงิน การคืนเงิน การเติมคืน และบัญชีของคุณ',
    ],
    vi: [
      'Điều khoản dịch vụ',
      'Những gì bạn đồng ý khi dùng PresenceRise: đơn hàng, wallet trả trước, hoàn tiền, bù hao và tài khoản của bạn.',
    ],
  },
  privacy: {
    en: [
      'Privacy Policy',
      'Exactly what PresenceRise stores about you, why, who sees it, and how long it is kept.',
    ],
    tl: [
      'Patakaran sa Privacy',
      'Kung ano mismo ang iniimbak ng PresenceRise tungkol sa iyo, bakit, sino ang nakakakita, at gaano katagal.',
    ],
    ceb: [
      'Patakaran sa Privacy',
      'Unsa gyud ang gitipigan sa PresenceRise bahin kanimo, ngano, kinsa ang makakita, ug hangtod kanus-a.',
    ],
    th: [
      'นโยบายความเป็นส่วนตัว',
      'PresenceRise เก็บอะไรเกี่ยวกับคุณบ้าง เพราะอะไร ใครเห็น และเก็บไว้นานแค่ไหน',
    ],
    vi: [
      'Chính sách bảo mật',
      'Chính xác PresenceRise lưu gì về bạn, vì sao, ai thấy, và giữ trong bao lâu.',
    ],
  },
  home: {
    en: [
      null,
      'Followers, likes, views and subscribers, priced per thousand, tracked live, and refunded or refilled on their own when they fall short.',
    ],
    tl: [
      null,
      'Followers, likes, views at subscribers, may presyo kada libo, live na sinusubaybayan, at kusang nirerefund o nirerefill kapag kulang.',
    ],
    ceb: [
      null,
      'Followers, likes, views ug subscribers, presyo kada libo, live nga gisubay, ug awtomatik nga girefund o girefill kung kulang.',
    ],
    th: [
      null,
      'ผู้ติดตาม ไลก์ ยอดวิว และผู้สมัคร คิดราคาต่อหนึ่งพัน ติดตามแบบเรียลไทม์ และคืนเงินหรือเติมคืนให้เองเมื่อส่งไม่ครบ',
    ],
    vi: [
      null,
      'Follower, lượt thích, lượt xem và người đăng ký, tính giá theo nghìn, theo dõi trực tiếp, và tự hoàn tiền hoặc bù hao khi giao thiếu.',
    ],
  },
  'public-services': {
    en: ['Services', 'Every service and every price, per 1,000, with nothing added at checkout.'],
    tl: ['Mga Serbisyo', 'Bawat serbisyo at bawat presyo, kada 1,000, walang idinadagdag sa checkout.'],
    ceb: ['Mga Serbisyo', 'Kada serbisyo ug kada presyo, kada 1,000, walay idugang sa checkout.'],
    th: ['บริการ', 'ทุกบริการและทุกราคา ต่อ 1,000 ไม่มีค่าใช้จ่ายเพิ่มตอนชำระเงิน'],
    vi: ['Dịch vụ', 'Mọi dịch vụ và mọi mức giá, theo 1.000, không phát sinh khi thanh toán.'],
  },
  faq: {
    en: ['FAQ', 'The questions people actually ask, answered plainly.'],
    tl: ['FAQ', 'Ang mga tanong na talagang itinatanong ng mga tao, sinagot nang simple.'],
    ceb: ['FAQ', 'Ang mga pangutana nga tinuod nga gipangutana sa mga tawo, gitubag nga yano.'],
    th: ['คำถามที่พบบ่อย', 'คำถามที่คนถามจริง ตอบตรงไปตรงมา'],
    vi: ['Câu hỏi thường gặp', 'Những câu mọi người thực sự hỏi, trả lời thẳng thắn.'],
  },
  help: {
    en: ['Help centre', 'Find your order or open a ticket, with or without an account.'],
    tl: ['Help centre', 'Hanapin ang order mo o mag-open ng ticket, may account man o wala.'],
    ceb: ['Help centre', 'Pangitaa ang imong order o mag-open ug ticket, adunay account man o wala.'],
    th: ['ศูนย์ช่วยเหลือ', 'ค้นหาออร์เดอร์ของคุณหรือเปิดตั๋ว มีบัญชีหรือไม่ก็ได้'],
    vi: ['Trung tâm trợ giúp', 'Tìm đơn của bạn hoặc mở ticket, có hoặc không có tài khoản.'],
  },
  login: {
    en: ['Sign in', 'Sign in to PresenceRise.'],
    tl: ['Mag-sign in', 'Mag-sign in sa PresenceRise.'],
    ceb: ['Mag-sign in', 'Mag-sign in sa PresenceRise.'],
    th: ['เข้าสู่ระบบ', 'เข้าสู่ระบบ PresenceRise'],
    vi: ['Đăng nhập', 'Đăng nhập PresenceRise.'],
  },
  register: {
    en: ['Create account', 'Order in under a minute. A username and a password is all it takes.'],
    tl: [
      'Gumawa ng account',
      'Mag-order sa wala pang isang minuto. Username at password lang ang kailangan.',
    ],
    ceb: [
      'Paghimo ug account',
      'Mag-order sa wala pay usa ka minuto. Username ug password ra ang kinahanglan.',
    ],
    th: ['สร้างบัญชี', 'สั่งซื้อได้ในไม่ถึงหนึ่งนาที ใช้แค่ชื่อผู้ใช้กับรหัสผ่าน'],
    vi: ['Tạo tài khoản', 'Đặt đơn trong chưa đầy một phút. Chỉ cần tên đăng nhập và mật khẩu.'],
  },
}

function setLink(rel, href, hreflang) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`
  let el = document.head.querySelector(selector)
  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    if (hreflang) el.hreflang = hreflang
    el.dataset.seo = '1'
    document.head.appendChild(el)
  }
  el.href = href
}

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function clearAlternates() {
  for (const el of document.head.querySelectorAll('link[data-seo="1"]')) el.remove()
}

/**
 * Rewrites the head for a route. Returns the page title it chose, or null
 * for a page that keeps the site name alone.
 */
export function applySeo(route, localeCode, siteName) {
  clearAlternates()

  const page = PUBLIC_PAGES[route.name]
  if (!page) {
    // Signed-in surfaces: never indexed, no alternates.
    setMeta('name', 'robots', 'noindex, nofollow')
    return null
  }

  setMeta('name', 'robots', 'index, follow')

  const locale = localeByCode(localeCode)
  // The language this page is actually in: the reader's if a translation
  // exists, else English. The canonical and the html lang must say that.
  const served = page.translated.includes(locale.code) ? locale.code : 'en'
  document.documentElement.lang = localeByCode(served).htmlLang

  setLink('canonical', `${SITE}${localisePath(served, page.path)}`)
  for (const code of page.translated) {
    const entry = LOCALES.find((l) => l.code === code)
    setLink('alternate', `${SITE}${localisePath(code, page.path)}`, entry.htmlLang)
  }
  setLink('alternate', `${SITE}${localisePath('en', page.path)}`, 'x-default')

  const [title, description] = COPY[route.name]?.[served] ?? COPY[route.name]?.en ?? [null, null]
  if (description) {
    setMeta('name', 'description', description)
    setMeta('property', 'og:description', description)
  }
  setMeta('property', 'og:title', title ? `${title} · ${siteName}` : siteName)
  setMeta('property', 'og:url', `${SITE}${localisePath(served, page.path)}`)
  setMeta('property', 'og:locale', localeByCode(served).htmlLang.replace('-', '_'))
  return title
}

/** Every indexable URL, for the sitemap. */
export function publicUrls() {
  const urls = []
  for (const page of Object.values(PUBLIC_PAGES)) {
    for (const code of page.translated) urls.push(`${SITE}${localisePath(code, page.path)}`)
  }
  return urls
}
